import { useState, useEffect } from "react";
import { Button, Col, Form, Input, Row, Select, Switch } from "antd";
import { InfiniteSelect } from "../../../../components/infiniteSelect";
import { Uploader } from "../../../../components/Upload";
import { usePostData } from "../../../../Network/usePostData";
import { useNavigate, useParams } from "react-router-dom";
import { useGetData } from "../../../../Network/useGetQueryData";
import { usePutData } from "../../../../Network/usePutData";
import { Loading } from "../../../../components/Loading";
import { maxLength, required } from "../../../../utils/form-validations";
import { useTranslation } from "react-i18next";

export const DetailsStep = ({
  onChangeStep,
}: {
  onChangeStep: (step: number) => void;
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>();
  const [goNext, setGoNext] = useState<boolean>();
  const [categoryId, setCategoryId] = useState<string | number>();
  const [imgUrl, setImgUrl] = useState<string>();
  const { mutateAsync } = usePostData({
    url: "service-provider",
    options: {
      onSuccess: (data) => {
        if (goNext) {
          onChangeStep(1);
        } else navigate("/providers");
      },
      onError: (error) => {},
    },
  });

  const { mutate } = usePutData<{ message: string }, { name: string }>({
    url: "service-provider",
    options: {
      onSuccess: (data) => {
        if (goNext) {
          onChangeStep(1);
        } else navigate("/providers");
      },
      onError: (error) => {},
    },
  });

  const { data: bankAccounts } = useGetData<{
    data: any;
  }>({
    queryKey: ["bank-accounts"],
    url: `bank-account?serviceProviderId=${id}&offset=0&limit=10&withDeleted=false`,
    options: {
      enabled: !!id,
      queryKey: ["bank-accounts"],
    },
  });

  const { data, isLoading } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-provider-details"],
    url: `service-provider/${id}`,
    options: {
      enabled: !!id,
      queryKey: ["service-provider-details"],
    },
  });

  const [form] = Form.useForm();

  useEffect(() => {
    if (!data || !id) return;

    const providerDetails = data?.data;

    form.setFieldsValue({
      ...providerDetails,
      bankAccounts: bankAccounts?.data?.items?.[0],
      phoneNumbers: providerDetails?.phoneNumbers?.[0],
      emails: providerDetails?.emails?.[0],
      status: providerDetails?.status === "active" ? true : false,
      categories: providerDetails?.categories?.[0]?.id,
      subCate: providerDetails?.categories?.[1]?.id,
    });
    setCategoryId(providerDetails?.categories?.[0]?.id);
    providerDetails?.logoUrl && setImgUrl(providerDetails?.logoUrl);
  }, [data, bankAccounts]);

  const handleAddProvider = async (values: any) => {
    setLoading(true);

    try {
      const providerData = {
        ...values,
        phoneNumbers: [values?.phoneNumbers],
        emails: [values?.emails],
        categories: [values?.categories, values?.subCate],
        type: "doctor",
        logoUrl: imgUrl,
        address: !!values?.address ? values?.address : undefined,
        websiteUrl: !!values?.websiteUrl ? values?.websiteUrl : undefined,
        bankAccounts: values?.accountNumber
          ? [
              {
                ...values.bankAccounts,
                branchName: "",
                accountHolderName: "",
                swiftCode: "",
                id: id && bankAccounts?.data?.items?.[0]?.id,
              },
            ]
          : [],
        contactPerson: { ...values.contactPerson, countryCode: "+20" },
        status: values.status ? "active" : "not_active",
      };

      if (id) mutate({ ...providerData, id: +id });
      else {
        const response = await mutateAsync(providerData); //@ts-ignore
        navigate(`/add-provider/${response?.data?.id}`);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <Form
      form={form}
      className={id ? "edit-view" : ""}
      layout="vertical"
      initialValues={{ status: "active" }}
    >
      <p className="form-section-title">{t("form.accountDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.providerName")}
            name={["name", "en"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.providerNameArabic")}
            name={["name", "ar"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(3000)]}
            label={t("form.description")}
            name={["description", "en"]}
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(3000)]}
            label={t("form.descriptionArabic")}
            name={["description", "ar"]}
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("form.category")}
            rules={[required]}
            name="categories"
          >
            <InfiniteSelect
              onChange={setCategoryId}
              url="category"
              optionsKeys={{ label: "name", value: "id" }}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.accountStatus")} name="status">
            <Switch defaultChecked />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required]}
            label={t("form.activity")}
            name="subCate"
          >
            <InfiniteSelect
              url="/category"
              optionsKeys={{ label: "name", value: "id" }}
              params={categoryId ? { parentId: categoryId } : {}}
              queryKey={["categoryId", categoryId?.toString()]}
              disabled={!categoryId}
              shouldFetch={!!categoryId}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.logo")} name="logoUrl">
            {imgUrl ? (
              <div className="logo-view-details">
                <img
                  src={imgUrl}
                  alt="logo-url"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/assets/providers/mokhtabar.png";
                  }}
                />
                <span
                  onClick={() => setImgUrl(undefined)}
                  className="update-img-link"
                >
                  x
                </span>
              </div>
            ) : (
              <Uploader onChange={setImgUrl} />
            )}
          </Form.Item>
        </Col>
      </Row>
      <p className="form-section-title">{t("form.providerDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              { len: 10, message: t("validation.phoneLength") },
            ]}
            label={t("form.phoneNumber")}
            name="phoneNumbers"
          >
            <Input prefix="+20" type="number" />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              maxLength(255),
              { type: "email", message: t("validation.email") },
            ]}
            label={t("form.email")}
            name="emails"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.address")} name="address">
            <Input.TextArea />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              maxLength(3000),
              {
                pattern: /^www\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message: t("validation.websiteFormat"),
              },
            ]}
            label={t("form.websiteUrl")}
            name="websiteUrl"
          >
            <Input.TextArea />
          </Form.Item>
        </Col>
      </Row>

      <p className="form-section-title">{t("form.contactPersonDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.name")}
            name={["contactPerson", "name"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              { len: 10, message: t("validation.phoneLength") },
            ]}
            name={["contactPerson", "phoneNumber"]}
            label={t("form.phoneNumber")}
          >
            <Input prefix="+20" type="number" />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[
              required,
              maxLength(255),
              { type: "email", message: t("validation.email") },
            ]}
            label={t("form.email")}
            name={["contactPerson", "email"]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <p className="form-section-title">{t("form.otherDetails")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.commercialRegister")}
            name="cr"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            rules={[required, maxLength(255)]}
            label={t("form.taxId")}
            name="taxNumber"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.bank")} name={["bankAccounts", "bankName"]}>
            <Select
              showSearch
              options={[
                { label: "CIB", value: "CIB" },
                { label: "QNB", value: "QNB" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item
            label={t("form.bankAccount")}
            name={["bankAccounts", "accountNumber"]}
            rules={[{ max: 255, message: t("validation.maxLength255") }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={24} xl={12}>
          <Form.Item label={t("form.bankIban")} name={["bankAccounts", "iBan"]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <div className="steps-action-footer">
        <Button
          loading={loading}
          type="primary"
          style={{ marginRight: "8px" }}
          onClick={async () => {
            try {
              const values = await form.validateFields();
              setGoNext(true);
              await handleAddProvider(values);
            } catch (error) {}
          }}
        >
          {t("form.next")}
        </Button>
        <Button
          onClick={async () => {
            try {
              const values = await form.validateFields();
              await handleAddProvider(values);
            } catch (error) {}
          }}
          loading={loading}
          type="link"
        >
          {t("form.saveAndClose")}
        </Button>
      </div>
    </Form>
  );
};
